import React, { useState } from 'react';
import { MapPin, Mail, Clock, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: '', // Honeypot field - hidden from users
    agreedToPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (checked) => {
    setFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToPrivacy) {
      toast({
        title: "Privacy Consent Required",
        description: "Please agree to the Privacy Policy to continue.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to backend which will email hello@paint-it-better.com
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        address: '',
        subject: `Website Enquiry from ${formData.name}`,
        message: formData.message,
        website: formData.website // Honeypot field
      });

      setIsSubmitted(true);
      toast({
        title: "Message Sent!",
        description: "Thank you for your enquiry. We'll get back to you soon.",
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or email us directly at hello@paint-it-better.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
        <SEO 
          title="Contact Paint It Better | Free Quote in Kent"
          description="Get a free, no-obligation quote for painting and decorating in Kent. Call, email or send us a message. Serving Wye, Ashford, Canterbury and surrounding areas."
          canonical="/contact"
        />
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Message Sent!
              </h1>
              <p className="text-xl text-cyan-100">
                Thank you for getting in touch.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <div className="flex justify-center mb-6">
                  <div className="bg-green-100 rounded-full p-4">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Thank You for Your Enquiry
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  We've received your message and will get back to you as soon as possible, usually within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <Button className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 rounded-full">
                      Back to Home
                    </Button>
                  </Link>
                  <Link to="/portfolio">
                    <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50 px-8 rounded-full">
                      View Our Work
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO 
        title="Contact Paint It Better | Free Quote in Kent"
        description="Get a free, no-obligation quote for painting and decorating in Kent. Call, email or send us a message. Serving Wye, Ashford, Canterbury and surrounding areas."
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Request a Decorating Quote in Kent
            </h1>
            <p className="text-xl text-cyan-100">
              Ready to transform your space? Contact us today for a free, no-obligation quote.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Send Us a Message
                </h2>
                <p className="text-slate-600 mb-8">
                  Please let us know if you would like to arrange a visit from us or a phone or video call to discuss your project. We would love to help!
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from users, catches bots */}
                  <div className="hidden" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="mt-2"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="mt-2"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      className="mt-2"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell us about your project..."
                      className="mt-2"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {/* Privacy Consent Checkbox */}
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="privacy"
                        checked={formData.agreedToPrivacy}
                        onCheckedChange={handleCheckbox}
                        className="mt-1"
                        data-testid="contact-privacy-checkbox"
                      />
                      <Label htmlFor="privacy" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                        I agree to the <Link to="/privacy" className="text-cyan-600 hover:text-cyan-700 underline">Privacy Policy</Link> and consent to Paint It Better contacting me about my enquiry. *
                      </Label>
                    </div>
                    <p className="text-xs text-slate-500 ml-6">
                      We only use your details to respond to your enquiry and never share them with third parties.
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full transition-all duration-300 hover:scale-105"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 h-5 w-5" />
                  </Button>
                </form>

                {/* Email Backup */}
                <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                  <p className="text-slate-600">
                    Prefer email? Contact us at{' '}
                    <a 
                      href="mailto:hello@paint-it-better.com" 
                      className="text-cyan-600 hover:text-cyan-700 font-semibold underline"
                    >
                      hello@paint-it-better.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-cyan-100 rounded-full p-3 flex-shrink-0">
                      <MapPin className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Location</h4>
                      <p className="text-slate-600">Wye, Ashford</p>
                      <p className="text-slate-600">Kent, TN25 5AS</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-cyan-100 rounded-full p-3 flex-shrink-0">
                      <Mail className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                      <a href="mailto:hello@paint-it-better.com" className="text-cyan-600 hover:text-cyan-700 transition-colors font-medium">
                        hello@paint-it-better.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-cyan-100 rounded-full p-3 flex-shrink-0">
                      <Clock className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Working Hours</h4>
                      <p className="text-slate-600">Monday - Friday: 8am - 6pm</p>
                      <p className="text-slate-600">Saturday: 9am - 4pm</p>
                      <p className="text-slate-600">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2501.234567890123!2d0.9410831!3d51.1818131!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDEwJzU0LjUiTiAwwrA1Nic0MC4wIkU!5e0!3m2!1sen!2suk!4v1234567890123!5m2!1sen!2suk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Paint It Better Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Carousel */}
      <ReviewCarousel />
    </div>
  );
};

export default Contact;
